import { useQuery } from "@tanstack/react-query";

import { useApiClient } from "~/hooks/use-api-client";
import { captureException } from "~/lib/analytics";
import { logger } from "~/lib/logger";

export interface Practitioner {
    id: string;
    fullName: string | null;
    email: string | null;
}

export interface Client {
    id: string;
    fullName: string | null;
    email: string | null;
}

export interface UserRelationshipsResponse {
    practitioners: Practitioner[];
    clients: Client[];
    isPractitioner: boolean;
    practitionerEnabledPartImages: boolean;
    selfEnabledPartImages: boolean;
}

export const useUserRelationships = () => {
    const apiClient = useApiClient();

    return useQuery<UserRelationshipsResponse, Error>({
        queryKey: ["user-relationships"],
        queryFn: async () => {
            try {
                const response = await apiClient.get("/user-relationships");

                if (!response.ok) {
                    if (response.status !== 401) {
                        const error = new Error(
                            `Failed to fetch relationships: ${response.statusText}`,
                        );
                        logger
                            .withError(error)
                            .error("Failed to fetch user relationships");
                        throw error;
                    }

                    return {
                        practitioners: [],
                        clients: [],
                        isPractitioner: false,
                        practitionerEnabledPartImages: false,
                        selfEnabledPartImages: false,
                    };
                }

                return (await response.json()) as UserRelationshipsResponse;
            } catch (error) {
                logger
                    .withError(error as Error)
                    .error("Error in user relationships query");
                captureException("Error fetching user relationships", error);
                throw error;
            }
        },
        // Data is prefetched on server via _protected.tsx loader
        staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
        initialDataUpdatedAt: 0,
    });
};
